import React from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  BarElement,
} from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";
import { Bar, getElementsAtEvent } from "react-chartjs-2";

// Register necessary plugins
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  annotationPlugin
);

interface ITurnoverGraph {
  data: any;
  onSelect: any;
}

export const TurnoverGraph: React.FC<ITurnoverGraph> = ({ data, onSelect }) => {
  const ref = React.useRef();

  const onClick = (e) => {
    if (getElementsAtEvent(ref.current!, e).length > 0) {
      console.log(getElementsAtEvent(ref.current!, e));
      let datasetIndex = getElementsAtEvent(ref.current!, e)[0].datasetIndex;
      let dataPoint = getElementsAtEvent(ref.current!, e)[0].index;

      console.log({
        datasetIndex,
        dataPoint,
        data: data[dataPoint],
      });

      onSelect(data[dataPoint]);
    }
  };

  // Helper function to calculate median
  const calculateMedian = (arr) => {
    const sorted = [...arr].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 !== 0
      ? sorted[mid]
      : (sorted[mid - 1] + sorted[mid]) / 2;
  };

  if (data) {
    // Extract turnover data for calculations
    const turnoverData = data?.map((scan) => scan.ca);

    // Calculate the average and median
    const average =
      turnoverData.reduce((a, b) => a + b, 0) / turnoverData.length;
    const median = calculateMedian(turnoverData);

    // Determine the vertical adjustment based on the proximity of the values
    const difference = Math.abs(average - median);
    let averageYAdjust = -10;
    let medianYAdjust = 10;

    // If values are close, increase the adjustment to prevent overlap
    if (difference < 5) {
      averageYAdjust = -20;
      medianYAdjust = 20;
    }

    return (
      <div>
        <Bar
          className="cursor-pointer"
          ref={ref}
          onClick={onClick}
          options={{
            responsive: true,
            maintainAspectRatio: true,
            plugins: {
              legend: {
                display: false,
              },
              tooltip: {
                backgroundColor: 'rgba(30, 41, 59, 0.95)',
                padding: 12,
                borderColor: 'rgba(51, 65, 85, 1)',
                borderWidth: 1,
                titleColor: '#fff',
                bodyColor: '#fff',
                cornerRadius: 8,
              },
              annotation: {
                annotations: {
                  averageLine: {
                    type: "line",
                    yMin: average,
                    yMax: average,
                    borderColor: "rgb(139, 92, 246)",
                    borderWidth: 2,
                    borderDash: [5, 5],
                    label: {
                      content: `Moyenne: ${average.toFixed(2)} €`,
                      display: true,
                      position: "end",
                      backgroundColor: "rgba(139, 92, 246, 0.9)",
                      color: "#fff",
                      yAdjust: averageYAdjust - 5,
                      xAdjust: -150,
                      borderRadius: 6,
                      padding: 6,
                      font: {
                        size: 11,
                        weight: 'bold',
                      },
                    },
                  },
                },
              },
            },
            scales: {
              x: {
                grid: {
                  display: false,
                },
                ticks: {
                  color: '#6B7280',
                  font: {
                    size: 11,
                  },
                },
              },
              y: {
                type: "linear" as const,
                display: true,
                position: "left" as const,
                border: {
                  display: false,
                },
                grid: {
                  color: 'rgba(0, 0, 0, 0.05)',
                  drawTicks: false,
                },
                ticks: {
                  color: '#6B7280',
                  font: {
                    size: 11,
                  },
                  padding: 8,
                  callback: function (value) {
                    return Number.isInteger(value) ? value + " €" : null;
                  },
                },
              },
            },
          }}
          data={{
            labels: data?.map((scan) => {
              // format scan.createdAt date to be displayed dd/mm/yyyy hh:mm
              let date = new Date(scan.createdAt);

              // Tableau des jours de la semaine
              const joursSemaine = [
                "dim",
                "lun",
                "mar",
                "mer",
                "jeu",
                "ven",
                "sam",
              ];

              // Récupérer les différentes parties de la date
              let jourSemaine = joursSemaine[date.getDay()]; // jour de la semaine
              let jour = date.getDate().toString().padStart(2, "0"); // jour du mois
              let mois = (date.getMonth() + 1).toString().padStart(2, "0"); // mois, +1 car les mois commencent à 0
              let annee = date.getFullYear(); // année

              return `${jourSemaine} ${jour}/${mois}`;
            }),
            datasets: [
              {
                label: "Chiffre d'affaires",
                data: turnoverData,
                backgroundColor: "rgba(139, 92, 246, 0.65)",
                borderColor: "rgba(139, 92, 246, 1)",
                borderWidth: 0,
                borderRadius: 6,
                hoverBackgroundColor: "rgba(139, 92, 246, 0.8)",
              },
            ],
          }}
        />
      </div>
    );
  }

  return null;
};
